import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import {
  OpenInNew,
  Delete,
  CheckCircle,
  Cancel,
  ArrowForward,
  Link as LinkIcon
} from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DiscoveryResults = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedSources, setSelectedSources] = useState(new Set());
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  const navigate = useNavigate();
  const location = useLocation();
  const query = location.state?.query || "Unknown";

  useEffect(() => {
    fetchDiscoveredSources();
  }, []);

  const fetchDiscoveredSources = async () => {
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("No authentication token");
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(`${API_URL}/api/resume-sourcing/discovery-results`, {
        params: { company_id },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSources(response.data.sources);
      
      // Auto-select all by default
      const allIds = new Set(response.data.sources.map(s => s.id));
      setSelectedSources(allIds);
      
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedSources.size === sources.length) {
      setSelectedSources(new Set());
    } else {
      const allIds = new Set(sources.map(s => s.id));
      setSelectedSources(allIds);
    }
  };

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedSources);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedSources(newSelected);
  };

  const handleRemoveSelected = async () => {
    if (selectedSources.size === 0) {
      setError("No sources selected to remove");
      return;
    }
    
    if (!window.confirm(`Remove ${selectedSources.size} selected candidate(s)? They will be rejected and not parsed.`)) {
      return;
    }
    
    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      await axios.post(`${API_URL}/api/resume-sourcing/reject-sources`, {
        source_ids: Array.from(selectedSources),
        // company_id: company_id
      },{
headers: {
          Authorization: `Bearer ${token}`
        }
      }
 
      );
      
      setSuccessMessage(`Removed ${selectedSources.size} candidate(s)`);
      
      // Refresh list
      setTimeout(() => {
        fetchDiscoveredSources();
        setSelectedSources(new Set());
        setSuccessMessage("");
      }, 1500);
      
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove sources");
    } finally {
      setProcessing(false);
    }
  };

  const handleProceedWithSelected = async () => {
    if (selectedSources.size === 0) {
      setError("No candidates selected to proceed");
      return;
    }
    
    if (!window.confirm(`Proceed with ${selectedSources.size} candidate(s)? They will be sent for parsing and added to your candidate list.`)) {
      return;
    }
    
    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      await axios.post(`${API_URL}/api/resume-sourcing/approve-sources`, {
        source_ids: Array.from(selectedSources),
      },{
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setSuccessMessage(`Approved ${selectedSources.size} candidate(s) for parsing. Parsing started in background.`);
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate("/candidates");
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.error || "Failed to approve sources");
    } finally {
      setProcessing(false);
    }
  };

  const handleVisitUrl = (url) => {
    window.open(url, '_blank');
  };

  const getSourceIcon = (url) => {
    if (url.includes('github')) return '🐙';
    if (url.includes('linkedin')) return '🔗';
    if (url.includes('stackoverflow')) return '📚';
    if (url.includes('medium')) return '📝';
    return '🌐';
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="1200px" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Discovery Results
        </Typography>
          {location.state?.searchParams && (
    <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
      <Typography variant="subtitle2" color="#ff9800" gutterBottom>
        Search Criteria:
      </Typography>
      <Typography variant="body2">
        <strong>Job Title:</strong> {location.state.searchParams.job_title}
      </Typography>
      {location.state.searchParams.experience && (
        <Typography variant="body2">
          <strong>Experience:</strong> {location.state.searchParams.experience}+ years
        </Typography>
      )}
      {location.state.searchParams.skills && (
        <Typography variant="body2">
          <strong>Skills:</strong> {location.state.searchParams.skills}
        </Typography>
      )}
      <Typography variant="body2">
        <strong>Job Description Required:</strong> {location.state.searchParams.require_jd ? 'Yes' : 'No'}
      </Typography>
    </Box>
  )}
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Search query: "{query}"
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        
        {sources.length === 0 ? (
          <Alert severity="info">
            No discovered candidates found. Try running a new discovery search.
          </Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip 
                  label={`${selectedSources.size} / ${sources.length} selected`} 
                  color="primary" 
                  variant="outlined"
                />
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  disabled={processing}
                >
                  {selectedSources.size === sources.length ? "Deselect All" : "Select All"}
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleRemoveSelected}
                  disabled={selectedSources.size === 0 || processing}
                >
                  Remove Selected
                </Button>
                
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ArrowForward />}
                  onClick={handleProceedWithSelected}
                  disabled={selectedSources.size === 0 || processing}
                >
                  Proceed with {selectedSources.size} Candidate(s)
                </Button>
              </Box>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSources.size === sources.length && sources.length > 0}
                        indeterminate={selectedSources.size > 0 && selectedSources.size < sources.length}
                        onChange={handleSelectAll}
                        disabled={processing}
                      />
                    </TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Discovered On</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sources.map((source, index) => (
                    <TableRow key={source.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSources.has(source.id)}
                          onChange={() => handleSelectOne(source.id)}
                          disabled={processing}
                        />
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {source.url.length > 60 ? source.url.substring(0, 60) + '...' : source.url}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getSourceIcon(source.url)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(source.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Visit URL">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleVisitUrl(source.url)}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body2" color="primary">
                💡 <strong>How it works:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1. Review each candidate by clicking the visit icon<br/>
                2. Uncheck any irrelevant or low-quality profiles<br/>
                3. Click "Proceed" to parse the selected candidates<br/>
                4. Parsing happens in background — candidates will appear in your dashboard
              </Typography>
            </Box>
          </>
        )}
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate("/candidates")}>
            Back to Dashboard
          </Button>
        </Box>
        
        {processing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress size={30} />
            <Typography sx={{ ml: 2 }}>Processing...</Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default DiscoveryResults;